import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Text,
    Spinner,
    Alert,
    AlertIcon,
    VStack,
    Divider,
    Image,
    Flex,
    Link, 
    Badge,
    Container,
    Tag,
    TagLabel,
} from "@chakra-ui/react";
import Navbar from "../navbar/Navbar";
import { AuthContext } from "../context/AuthContext";
import api from "../utils/axios";
import { parseApiDate } from "../utils/date";

const getStatusText = (status) => {
    const statusMap = {
        0: "Reported",
        10: "In Progress",
        20: "Cancelled",
        30: "Resolved",
        40: "Archived",
    };
    return statusMap[status] || "Unknown";
};

const TicketDetails = () => {
    const { ticketId } = useParams();
    const { isAuthenticated, user } = useContext(AuthContext);
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { currLocation } = useContext(AuthContext);
    
    const hasFetched = useRef(false);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const fetchTicket = async () => {
            try {
                const response = await api.get(`tickets/${ticketId}/location/${currLocation}`, {
                    method: "GET",
                    headers: {
                        "Cache-Control": "no-cache, no-store, must-revalidate",
                        "Pragma": "no-cache",
                        "Expires": "0",
                    },
                });

                if (!response.data || typeof response.data !== "object") {
                    throw new Error("Invalid or unexpected API response.");
                }

                setTicket(response.data);
                setError(null);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchTicket();
    }, [ticketId]);

    return (
        <>
            {isAuthenticated && <Navbar />}

            <Container 
                maxW={{ base: "90%", md: "600px", lg: "700px" }} 
                p={{ base: 4, md: 6 }} 
                marginTop={isAuthenticated ? { base: "25rem", md: "80px", lg: "70px" } : "0px"}
            >
                <Box
                    bg="white"
                    boxShadow="card"
                    borderRadius="lg"
                    p={{ base: 4, md: 6 }}
                    marginTop={'5em'}
                    marginBottom={'5em'}
                    textAlign={{ base: "center", md: "left" }}
                >
                    {loading ? (
                        <Flex justify="center" align="center" height="200px">
                            <Spinner size="xl" />
                        </Flex>
                    ) : error ? (
                        <Alert status="error">
                            <AlertIcon />
                            {error}
                        </Alert>
                    ) : ticket && typeof ticket === "object" ? (
                        <VStack spacing={5} align="stretch">
                            <Badge 
                                colorScheme="blue"
                                fontSize="md"
                                p={3} 
                                borderRadius="8px"
                                textAlign="center"
                                alignSelf="center"
                                w="fit-content"
                            >
                                {getStatusText(ticket.status)}
                            </Badge>

                            <Text 
                                fontSize={{ base: "2xl", md: "3xl" }} 
                                fontWeight="bold" 
                                color="gray.800"
                                textAlign="center"
                            >
                                {ticket.name}
                            </Text>

                            <Divider />
                            <Text fontSize="sm" color="gray.500">
                                <Tag 
                                    size="sm"
                                    borderRadius="md"
                                    bg="blue.50"
                                    color="brand.700"
                                    border="1px solid"
                                    borderColor="blue.200"
                                    px={2}
                                    py={1}
                                    fontWeight="semibold"
                                >
                                    <TagLabel>{ticket.sectionName}</TagLabel>
                                </Tag>
                            </Text>

                            <Text fontSize={{ base: "md", md: "lg" }} color="gray.700">
                                {ticket.description}
                            </Text>


                            <Text fontSize="sm" color="gray.500">
                                Kreirano: <strong>{parseApiDate(ticket.createdAt).toLocaleString("hr-HR", { timeZone: "Europe/Zagreb" })}</strong>
                            </Text>

                            <Text fontSize="sm" color="gray.500">
                                Kreirano: <strong>{ticket.createdByFullName}</strong>
                            </Text>

                            <Text fontSize="sm" color="gray.500">
                                Kontakt: <strong>
                                    <Link 
                                        href={`tel:${ticket.createdByPhone}`} 
                                        display="inline-flex" 
                                        alignItems="center"
                                    >
                                        {ticket.createdByPhone} (klikni za pozvati)
                                    </Link>
                                </strong>
                            </Text>

                            {ticket.assignedTo && (
                                <Text fontSize="sm" color="gray.500">
                                    Dodjeljeno: <strong>{ticket.assignedTo}</strong>
                                </Text>
                            )}

                            {isAuthenticated && user && (
                                <Text fontSize="sm" color="gray.500">
                                    Prijavljen kao: <strong>{user.fullName}</strong>
                                </Text>
                            )}

                            {ticket.imageUrl && (
                                <Box
                                    w="100%"
                                    borderRadius="lg"
                                    overflow="hidden"
                                    mt={4}
                                    display="flex"
                                    justifyContent="center"
                                >
                                    <Image
                                        src={ticket.imageUrl}
                                        alt="Slika zadatka"
                                        objectFit="cover"
                                        w={{ base: "100%", md: "80%" }}
                                        borderRadius="10px"
                                    />
                                </Box>
                            )}
                        </VStack>
                    ) : (
                        <Alert status="error">
                            <AlertIcon />
                            Nevalidan zadatak/kvar.
                        </Alert>
                    )}
                </Box>
            </Container>
        </>
    );
};

export default TicketDetails;
